// Lead Detail Page
// Refactored to use modular components from /components/lead/

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

// Import all lead components
import {
  LeadScoreCard,
  CaseSummary,
  FollowUpHistory,
  LeadActions,
  ClientInfoCard,
  AccidentDetails,
  InjuryDetails,
  DocumentsList,
  ConsultationStatus,
  CMSSyncStatus,
  PageHeader,
  MobileCallFAB,
  CMS_FORMATS
} from '@/components/lead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [intake, setIntake] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [generatingRetainer, setGeneratingRetainer] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [intakeRes, followUpsRes] = await Promise.all([
        axios.get(`${API}/intakes/${id}`),
        axios.get(`${API}/follow-ups/intake/${id}`)
      ]);
      setIntake(intakeRes.data);
      setFollowUps(followUpsRes.data);
    } catch (error) {
      toast.error('Failed to load lead details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Follow-up actions
  const cancelFollowUp = async (followUpId) => {
    try {
      await axios.post(`${API}/follow-ups/${followUpId}/cancel`);
      toast.success('Follow-up cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel follow-up');
    }
  };

  const resendFollowUp = async (followUpId) => {
    try {
      await axios.post(`${API}/follow-ups/${followUpId}/resend`);
      toast.success('Follow-up resent');
      fetchData();
    } catch (error) {
      toast.error('Failed to resend follow-up');
    }
  };

  // Status update
  const updateStatus = async (field, value) => {
    setUpdating(true);
    try {
      const response = await axios.patch(`${API}/intakes/${id}/status`, { [field]: value });
      setIntake(response.data);
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Generate retainer agreement
  const generateRetainer = async (bilingual = false) => {
    setGeneratingRetainer(true);
    try {
      const endpoint = bilingual 
        ? `${API}/retainer/${id}/bilingual`
        : `${API}/retainer/generate/${id}`;
      
      const response = await axios.post(endpoint, {});
      
      // Convert base64 to blob and download
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const successMsg = bilingual 
        ? `Bilingual retainer (English + ${response.data.languages?.secondary}) downloaded!`
        : 'Retainer agreement downloaded!';
      toast.success(successMsg);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate retainer agreement');
    } finally {
      setGeneratingRetainer(false);
    }
  };

  // Delete lead (GDPR/CCPA)
  const handleDeleteLead = async () => {
    setDeletingLead(true);
    try {
      await axios.delete(`${API}/compliance/lead/${id}`);
      toast.success('Lead data deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete lead data');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingLead(false);
    }
  };

  // Export intake to CMS
  const exportIntake = async (cmsType = null) => {
    try {
      const url = cmsType 
        ? `${API}/export/intake/${id}?cms_type=${cmsType}`
        : `${API}/export/intake/${id}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = cmsType 
        ? `IntakePal_Export_for_${cmsType}.csv`
        : 'IntakePal_Universal_Export.csv';
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Download the file
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(cmsType 
        ? `Downloaded ${CMS_FORMATS.find(f => f.id === cmsType)?.name || cmsType} format`
        : 'Downloaded universal CSV'
      );
    } catch (error) {
      toast.error('Failed to export intake');
    }
  };

  // Helper function
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (!intake) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <ClientInfoCard 
              intake={intake}
              formatDate={formatDate}
              generatingRetainer={generatingRetainer}
              onGenerateRetainer={generateRetainer}
              onExportIntake={exportIntake}
            />
            
            <CaseSummary intake={intake} />
            
            <AccidentDetails intake={intake} />
            
            <InjuryDetails intake={intake} />
            
            <DocumentsList fileUrls={intake.file_urls} />
            
            <FollowUpHistory 
              followUps={followUps}
              onCancel={cancelFollowUp}
              onResend={resendFollowUp}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <LeadScoreCard intake={intake} />
            
            <LeadActions 
              intake={intake}
              updating={updating}
              deletingLead={deletingLead}
              showDeleteConfirm={showDeleteConfirm}
              setShowDeleteConfirm={setShowDeleteConfirm}
              onStatusUpdate={updateStatus}
              onDeleteLead={handleDeleteLead}
            />
            
            <ConsultationStatus intake={intake} />
            
            <CMSSyncStatus cmsSyncs={intake.cms_syncs} />
          </div>
        </div>
      </main>

      <MobileCallFAB clientPhone={intake.client_phone} />
    </div>
  );
};

export default LeadDetailPage;
